import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { GUIDE_SEEN_KEY, STEPS, stepTitle } from './BriefGuide'
import { ARROWCURVE, ARROWLOOP, ARROWS, Doodle, SQUIGGLE } from './doodles'
import styles from './BriefTourDiagram.module.css'

/*
 * The brief tour as a sketchnote (demo option): a slim band above the brief, on the page's grey canvas,
 * where a note for each area is jotted straight onto the page in marker. "Start with…" is circled; the other
 * notes each have an arrow beside them, on whichever side their area lies. Notes are staggered a little so they
 * don't look stamped out, and appear in reading order (start, read, ask). It measures where the areas are
 * (elements marked data-guide="…") and follows them as the layout changes: a note sits over its area, its
 * arrow turns to face it, and an area that isn't on the page (notes hidden, say) has no note. Mission Control
 * only has one as the side panel, not as the stacked view's floating bar or the compact bubble, and the AI
 * notes only while they sit beside the brief, not once small screens put them in a single column.
 */

// the areas below the band; the view controls are left to the spotlight tour
const DIAGRAM_STEPS = STEPS.filter(step => step.target !== 'view')

const NOTE_W = 230
const MIN_NOTE_W = 170 // narrower than this and the notes can't be read: the band steps aside
const GAP = 56 // room between notes for the arrows that sit beside the text
const MIN_LEFT = 48 // room on the left for an arrow beside the first note
const RIGHT_GUTTER = 56 // room on the right for an arrow beside the last note

/* the notes with an arrow (by step order): the doodle, and the side it's drawn for */
const ARROW_FOR: Record<number, { doodle: Doodle; side: 'left' | 'right'; className: string } | undefined> = {
  0: { doodle: ARROWS, side: 'right', className: styles.downArrow },
  1: { doodle: ARROWLOOP, side: 'right', className: styles.loopArrow },
  2: { doodle: ARROWCURVE, side: 'left', className: styles.curveArrow },
}

/* a traced mark: filled in ink, with a hairline stroke so it stays legible however small it's drawn.
   A "fit" mark is drawn to its doodle's own box (the ink spills outside it), so the box can match the text. */
const Mark: FunctionComponent<{ doodle: Doodle; className?: string; stretch?: boolean; fit?: boolean }> = ({
  doodle,
  className,
  stretch,
  fit,
}) => (
  <svg
    viewBox={fit ? `0 0 ${doodle.w} ${doodle.h}` : `-4 -4 ${doodle.w + 8} ${doodle.h + 8}`}
    preserveAspectRatio={stretch ? 'none' : 'xMidYMid meet'}
    className={`${styles.mark} ${className ?? ''}`}
    aria-hidden='true'
  >
    <path d={doodle.d} vectorEffect='non-scaling-stroke' />
  </svg>
)

interface Props {
  /** changes when the layout does (view, notes), so the diagram re-measures promptly */
  layoutKey?: string
  /** the note for the AI assistant, under the name it's been given */
  missionTitle?: string
}

export const BriefTourDiagram: FunctionComponent<Props> = ({ layoutKey, missionTitle }) => {
  const bandRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [xs, setXs] = useState<Record<string, number> | null>(null)

  // follow the areas as the layout settles or changes (cheap: three measurements, state only on change)
  useEffect(() => {
    let raf = 0
    let last = ''
    const measure = () => {
      const band = bandRef.current
      if (band) {
        const b = band.getBoundingClientRect()
        const next: Record<string, number> = {}
        DIAGRAM_STEPS.forEach(step => {
          let el = document.querySelector<HTMLElement>(`[data-guide="${step.target}"]`)
          if (!el) return // not on the page (e.g. notes hidden, or full screen): no note for it
          // Mission Control only gets a note as the side panel beside the brief: as the stacked view's floating
          // bar (fixed) or the compact bubble (its wrapper folded away with display: contents) it's left out
          if (step.target === 'mission') {
            const { display, position } = window.getComputedStyle(el)
            if (display === 'contents' || position === 'fixed') return
          }
          // the AI notes only get a note while they sit beside the brief; on small screens they drop into the
          // single column under each section (no longer positioned), and there's nothing beside it to point at
          if (step.target === 'notes' && window.getComputedStyle(el).position !== 'absolute') return
          if (el.children.length === 1) el = el.children[0] as HTMLElement
          const r = el.getBoundingClientRect()
          next[step.target] = r.width > 0 ? r.left + r.width / 2 - b.left : -1
        })
        const key = `${Math.round(b.width)}|${Object.entries(next)
          .map(([k, x]) => `${k}:${Math.round(x)}`)
          .join(',')}`
        if (key !== last) {
          last = key
          setWidth(b.width)
          setXs(next)
        }
      }
      if (typeof window.requestAnimationFrame === 'function') raf = window.requestAnimationFrame(measure)
    }
    measure()
    return () => {
      if (raf) window.cancelAnimationFrame(raf)
    }
  }, [layoutKey])

  // the diagram has no close of its own (the Tour button hides it): once shown, it doesn't open by itself again
  useEffect(() => {
    try {
      localStorage.setItem(GUIDE_SEEN_KEY, 'true')
    } catch {
      // storage unavailable; the tour may show again next time
    }
  }, [])

  // only the areas on the page get a note (before the first measure, assume all are); one without a size yet
  // (still laying out) takes an even share of the band
  const shown = DIAGRAM_STEPS.map((step, i) => ({ step, i })).filter(({ step }) => !xs || step.target in xs)
  const placed = shown.map(({ step, i }, n) => {
    const measured = xs?.[step.target]
    return { step, i, x: measured !== undefined && measured >= 0 ? measured : ((n + 0.5) * width) / shown.length }
  })
  const ordered = placed.sort((a, b) => a.x - b.x)

  // notes narrow to share the band; when they'd be too narrow to read, the band steps aside
  const noteW = ordered.length
    ? Math.min(NOTE_W, (width - MIN_LEFT - RIGHT_GUTTER - GAP * (ordered.length - 1)) / ordered.length)
    : NOTE_W
  const tooNarrow = width > 0 && noteW < MIN_NOTE_W

  // each note sits over its area, nudged along so none overlap or run off the band
  const lefts: number[] = []
  ordered.forEach(({ x }, k) => {
    let left = Math.max(MIN_LEFT, x - noteW / 2)
    if (k > 0) left = Math.max(left, lefts[k - 1] + noteW + GAP)
    lefts.push(left)
  })
  const overflow = lefts.length ? lefts[lefts.length - 1] + noteW - (width - RIGHT_GUTTER) : 0
  if (overflow > 0) for (let k = lefts.length - 1; k >= 0; k -= 1) lefts[k] = Math.max(MIN_LEFT, lefts[k] - overflow)

  // an arrow goes on the side its area lies (or its usual side, when the area is straight below the note)
  const arrowSide = (i: number, x: number, left: number): 'left' | 'right' => {
    const usual = ARROW_FOR[i]?.side ?? 'right'
    const offset = x - (left + noteW / 2)
    if (Math.abs(offset) < noteW / 6) return usual
    return offset > 0 ? 'right' : 'left'
  }

  // the band stays in the page (so it can keep measuring) but folds away when there's nothing it can show
  const folded = tooNarrow || ordered.length === 0
  if (folded) return <div ref={bandRef} className={styles.bandFolded} aria-hidden='true' />

  return (
    <div ref={bandRef} className={styles.band} role='region' aria-label='Brief tour'>
      <ul className={styles.notes}>
        {ordered.map(({ step, i, x }, k) => {
          const arrow = ARROW_FOR[i]
          const side = arrowSide(i, x, lefts[k] ?? 0)
          return (
            // i is the step's order (start, read, ask), so the notes appear in that order wherever they sit
            <li
              key={step.target}
              className={`${styles.note} ${styles[`d${i}`]}`}
              ref={el => {
                if (el) {
                  el.style.left = `${lefts[k] ?? 0}px`
                  el.style.width = `${noteW}px`
                }
              }}
            >
              <div className={styles.text}>
                <strong className={styles.title}>
                  {stepTitle(step, missionTitle)}
                  {i === 1 && <Mark doodle={SQUIGGLE} stretch className={styles.squiggle} />}
                </strong>
                <span className={styles.short}>{step.short}</span>

                {/* the arrow sits beside the text, on the side of its area; drawn mirrored when that isn't its usual side */}
                {arrow && (
                  <Mark
                    doodle={arrow.doodle}
                    className={[
                      styles.arrow,
                      arrow.className,
                      side === 'right' ? styles.arrowRight : styles.arrowLeft,
                      side !== arrow.side && styles.flip,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  />
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
